import { useContext, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Autocomplete, Checkbox, MenuItem, Stack, TextField } from "@mui/material";
import { CheckBoxOutlineBlank, CheckBox } from "@mui/icons-material";
import { useNode } from "@craftjs/core";
import { UserOrganizationContext } from "@/features/users/UserOrganizationProvider";
import { useAppSelector } from "@/hooks";
import { Content as IContent, ContentStatus, Category, OrgCategory } from "@/types";

const ContentSettings = () => {
  const {
    actions: { setProp },
    propValue: { selectedCategories = [], selectedContent = [], limit, sortBy = "price", sort = "descending", imageSort },
  } = useNode((node) => ({
    propValue: node.data.props,
  }));

  const { pathname } = useLocation();
  const { templateId, organizationId } = useParams();

  const org = useAppSelector((state) => state.organizations[organizationId ?? ""]);
  const categories = useAppSelector((state) => state.categories) as Record<string, Category>;
  const orgCategories = org.categories as OrgCategory[];

  const categoriesByIndustry = useMemo(() => {
    return Object.values(categories).filter((c) => c.industry === org.industry);
  }, [categories, org.industry]);

  const mixedCategories = orgCategories?.concat(categoriesByIndustry);

  const { content } = useContext(UserOrganizationContext);

  const limitPoints = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  const publishedContent = useMemo(() => {
    if (!content) return [];
    return content.filter(({ status }) => status === ContentStatus.PUBLISHED);
  }, [content]);

  const contentByCategory = useMemo(() => {
    if (!publishedContent) return {};

    if (!selectedCategories.length) {
      return {
        all: publishedContent,
      };
    }

    return publishedContent.reduce((acc, curr) => {
      if (selectedCategories.includes(curr.category)) {
        return {
          ...acc,
          [curr.category]: [...(acc[curr.category] ?? []), curr],
        };
      }

      return acc;
    }, {} as Record<IContent["category"], IContent[]>);
  }, [selectedCategories, publishedContent]);

  const availableContent = useMemo(() => {
    return Object.values(contentByCategory).reduce((acc, curr) => {
      return [...acc, ...curr];
    }, []);
  }, [contentByCategory]);

  //order content by image first and no image last
  // const sortContentByImage = useMemo(() => {
  //   return availableContent.sort((a, b) => {
  //     if (a.images && !b.images) return -1;
  //     if (!a.images && b.images) return 1;
  //     return 0;
  //   });
  // }, [availableContent]);
  // console.log("~ sortContentByImage:", sortContentByImage);

  const handleCategoryChange = (_: unknown, selectedValues: Category[] | OrgCategory[]) => {
    const content = selectedContent.map((id: IContent["id"]) => publishedContent.find((pc) => pc.id === id));
    const categoryIdList = selectedValues.map(({ id }) => id);
    const contentIdList: string[] = [];
    if (selectedValues.length) {
      content.forEach((content: IContent) => {
        if (content) {
          if (categoryIdList.includes(content.category)) {
            contentIdList.push(content.id);
          }
        }
      });
    }

    setProp((props: Props) => {
      if (selectedValues.length) props.selectedContent = contentIdList;
      props.selectedCategories = categoryIdList;
      return props;
    });
  };

  const handleContentChange = (_: unknown, selectedValues: IContent[]) => {
    const contentIdList = selectedValues.map(({ id }) => id);

    setProp((props: Props) => {
      props.selectedContent = contentIdList;
      return props;
    });
  };

  const selectedCategoryValues = useMemo(() => {
    return selectedCategories.map((selectedCategoryId: string) => mixedCategories?.find((c) => c.id === selectedCategoryId)).filter((c: Category) => !!c);
  }, [selectedCategories, mixedCategories]);

  const selectedContentValues = useMemo(() => {
    return selectedContent.map((id: IContent["id"]) => publishedContent.find((p) => p.id === id));
  }, [selectedContent, publishedContent]);

  return (
    <Stack spacing={2}>
      <Autocomplete
        size="small"
        multiple
        openOnFocus
        autoHighlight
        disableCloseOnSelect
        options={mixedCategories ?? []} //
        // options={categoriesByIndustry ?? []} //
        getOptionLabel={(option) => option?.title}
        onChange={handleCategoryChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} style={{ marginRight: 8 }} checked={selected} size="small" />
            {option.title}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Categorías" placeholder="Elige una o más categorías" size="small" />}
        value={selectedCategoryValues}
      />

      <Autocomplete
        size="small"
        disabled={pathname !== `/plantillas/${templateId}/${organizationId}/editor`}
        multiple
        openOnFocus
        autoHighlight
        disableCloseOnSelect
        options={availableContent}
        getOptionLabel={(option) => option?.title}
        onChange={handleContentChange}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} style={{ marginRight: 8 }} checked={selected} />
            {option?.title}
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Contenido" placeholder="Elige uno o mas platos" size="small" />}
        groupBy={(c) => {
          if (c?.category && categoriesByIndustry[c?.category]) {
            return categoriesByIndustry[c?.category].title;
          }
          return "";
        }}
        value={selectedContentValues}
      />

      <Stack direction={"row"} spacing={2}>
        <TextField
          select
          size="small"
          fullWidth
          label="Ordenar por campo"
          name="sort-by"
          value={sortBy}
          onChange={(e) => {
            setProp((props: Props) => {
              props.sortBy = e.target.value;
              return props;
            });
          }}
        >
          <MenuItem value="order">Orden</MenuItem>
          <MenuItem value="title">Título</MenuItem>
          <MenuItem value="price">Precio</MenuItem>
          <MenuItem value="none">Ninguno</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          fullWidth
          label="Orden"
          name="sort"
          value={sort}
          defaultValue={"descending"}
          onChange={(e) => {
            setProp((props: Props) => {
              props.sort = e.target.value;
              return props;
            });
          }}
        >
          <MenuItem value="descending">Descendente</MenuItem>
          <MenuItem value="ascending">Ascendente</MenuItem>
          <MenuItem value="none">Ninguno</MenuItem>
        </TextField>
      </Stack>

      <TextField
        select
        size="small"
        name="limit"
        label="Límite"
        value={limit ? limit : "all"}
        onChange={(e) =>
          setProp((props: Props) => {
            props.limit = e.target.value;
            return props;
          })
        }
      >
        <MenuItem value={"all"}>Todas</MenuItem>
        {limitPoints.map((limit, index) => (
          <MenuItem key={`${limit}-${index}`} value={limit}>
            {limit}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
};

export default ContentSettings;
